import React, { useState } from "react";

const S = {
  wrapper: {
    marginTop: '10px', marginLeft: '0px', border: '3px', padding: '40px',
    borderStyle: 'solid', borderColor: 'grey', fontFamily: "'Georgia', serif",
    color: '#1a1a2e', lineHeight: '1.7',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2E75B6',
    paddingBottom: '8px', marginBottom: '24px', color: '#1F3864',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#2E75B6', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2E75B6', paddingLeft: '10px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#EBF3FB', border: '2px solid #2E75B6', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#1F3864', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px', borderLeft: '2px solid #2E75B6',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.92rem' },
  th: { background: '#2E75B6', color: '#fff', padding: '10px 14px', textAlign: 'left', fontWeight: '700', fontSize: '0.9rem' },
  tdE: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#f5f9ff' },
  tdO: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#fff' },
  tdL: { fontWeight: '600', color: '#1F3864', whiteSpace: 'nowrap', width: '220px' },
  code: { fontFamily: "'Courier New', monospace", fontSize: '0.88rem', whiteSpace: 'pre-wrap' },
  dWrap: {
    margin: '24px 0', background: '#f8fbff', border: '1px solid #ccd9ea',
    borderRadius: '8px', padding: '20px',
  },
  dTitle: {
    fontWeight: '700', fontSize: '0.95rem', color: '#1F3864',
    marginBottom: '12px', textAlign: 'center', letterSpacing: '0.02em',
  },
  dCaption: {
    fontSize: '0.84rem', color: '#555', textAlign: 'center', marginTop: '10px', fontStyle: 'italic',
  },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width: '220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i % 2 === 0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Reusable axis grid
function Grid({ ox, oy, sc, xTicks, yTicks, W, H }) {
  return (
    <g>
      {xTicks.map(v => v !== 0 && (
        <g key={v}>
          <line x1={ox+v*sc} y1={10} x2={ox+v*sc} y2={H-10} stroke="#eee" strokeWidth="1"/>
          <text x={ox+v*sc} y={oy+13} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>
        </g>
      ))}
      {yTicks.map(v => v !== 0 && (
        <g key={v}>
          <line x1={10} y1={oy-v*sc} x2={W-10} y2={oy-v*sc} stroke="#eee" strokeWidth="1"/>
          <text x={ox-5} y={oy-v*sc+3} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>
        </g>
      ))}
      <line x1={10} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
      <line x1={ox} y1={H-10} x2={ox} y2={10} stroke="#1F3864" strokeWidth="1.5"/>
      <text x={W-8} y={oy-4} fontSize="11" fill="#1F3864" fontWeight="700">x</text>
      <text x={ox+4} y={14} fontSize="11" fill="#1F3864" fontWeight="700">y</text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Line Anatomy
// ─────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 520, H = 280, ox = 140, oy = 200, sc = 55;
  const toX = x => ox + x * sc;
  const toY = y => oy - y * sc;

  // y = (2/3)x + 2  — clear slope and intercepts
  const m = 2/3, b = 2;
  const f = x => m*x + b;

  // two points for rise/run triangle
  const p1x = 0, p2x = 3;
  const p1y = f(p1x), p2y = f(p2x);

  // x-intercept: 0 = mx+b → x = -b/m = -3
  const xInt = -b / m;

  // line extent
  const x1 = -3.2, x2 = 5.2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Line Anatomy — y = (2/3)x + 2</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <defs>
            <marker id="la" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/>
            </marker>
          </defs>

          <Grid ox={ox} oy={oy} sc={sc} xTicks={[-3,-2,-1,0,1,2,3,4,5]} yTicks={[-2,-1,0,1,2,3,4]} W={W} H={H}/>

          {/* Main line */}
          <line x1={toX(x1)} y1={toY(f(x1))} x2={toX(x2)} y2={toY(f(x2))}
            stroke="#2E75B6" strokeWidth="2.8"/>

          {/* Rise/run triangle */}
          <line x1={toX(p1x)} y1={toY(p1y)} x2={toX(p2x)} y2={toY(p1y)}
            stroke="#e07b00" strokeWidth="2" strokeLinecap="round"/>
          <line x1={toX(p2x)} y1={toY(p1y)} x2={toX(p2x)} y2={toY(p2y)}
            stroke="#c0392b" strokeWidth="2" strokeLinecap="round"/>
          <line x1={toX(p1x)} y1={toY(p1y)} x2={toX(p2x)} y2={toY(p2y)}
            stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.4"/>

          {/* Run label */}
          <rect x={toX(p1x)+4} y={toY(p1y)+5} width={44} height={17} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={toX(p1x+1.5)} y={toY(p1y)+17} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">run = 3</text>

          {/* Rise label */}
          <rect x={toX(p2x)+6} y={toY((p1y+p2y)/2)-8} width={44} height={17} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
          <text x={toX(p2x)+28} y={toY((p1y+p2y)/2)+5} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">rise = 2</text>

          {/* Slope callout */}
          <rect x={toX(2.2)} y={toY(2.6)} width={130} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.3"/>
          <text x={toX(2.2)+65} y={toY(2.6)+14} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">m = rise/run = 2/3</text>
          <text x={toX(2.2)+65} y={toY(2.6)+28} textAnchor="middle" fontSize="10" fill="#555">slope = 0.667</text>

          {/* Y-intercept */}
          <circle cx={toX(0)} cy={toY(b)} r={7} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
          <rect x={toX(0)+10} y={toY(b)-18} width={110} height={32} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.2"/>
          <text x={toX(0)+65} y={toY(b)-5} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">y-intercept = b = 2</text>
          <text x={toX(0)+65} y={toY(b)+9} textAnchor="middle" fontSize="9" fill="#27ae60">(0, 2) — where line hits y-axis</text>

          {/* X-intercept */}
          <circle cx={toX(xInt)} cy={oy} r={7} fill="#9b59b6" stroke="#fff" strokeWidth="2"/>
          <rect x={toX(xInt)-120} y={oy+10} width={112} height={30} rx={4} fill="#f9f0ff" stroke="#9b59b6" strokeWidth="1.2"/>
          <text x={toX(xInt)-64} y={oy+24} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">x-intercept = -3</text>
          <text x={toX(xInt)-64} y={oy+37} textAnchor="middle" fontSize="9" fill="#9b59b6">set y=0, solve for x</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Every feature of a line maps to a value in y = mx + b. The slope m is the rise over run between any two points. The y-intercept b is where it crosses the y-axis. The x-intercept is found by setting y = 0.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Three Forms of a Line
// ─────────────────────────────────────────────────────────────────
function DiagramForms() {
  const W = 540, H = 240;
  // Same line: slope 2, passing through (1, 3) → y = 2x + 1
  const m = 2, px = 1, py = 3, b = py - m*px; // b=1
  const toX = (x, ox, sc) => ox + x*sc;
  const toY = (y, oy, sc) => oy - y*sc;

  const panels = [
    {
      label: 'Slope-Intercept Form',
      eq: 'y = mx + b',
      filled: 'y = 2x + 1',
      note: 'm = 2,  b = 1',
      color: '#2E75B6',
      highlight: 'b',
    },
    {
      label: 'Point-Slope Form',
      eq: 'y - y₁ = m(x - x₁)',
      filled: 'y - 3 = 2(x - 1)',
      note: 'using point (1, 3)',
      color: '#c0392b',
      highlight: 'point',
    },
    {
      label: 'Standard Form',
      eq: 'Ax + By = C',
      filled: '-2x + y = 1',
      note: 'A=-2, B=1, C=1',
      color: '#27ae60',
      highlight: 'none',
    },
  ];

  const ox = 50, oy = 160, sc = 28;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Three Forms of a Line — Same Line, Different Emphasis</div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {panels.map(({ label, eq, filled, note, color, highlight }, pi) => {
          const cW = 160, cH = 200;
          const f = x => m*x + b;
          const pts = [];
          for (let xi = -1.5; xi <= 4; xi += 0.1) {
            const sx = toX(xi, ox, sc), sy = toY(f(xi), oy, sc);
            if (sy > 8 && sy < cH-8) pts.push(`${sx},${sy}`);
          }
          const path = pts.length > 1 ? 'M ' + pts.join(' L ') : '';
          const yInt = b, xInt = -b/m;
          const ptSx = toX(px, ox, sc), ptSy = toY(py, oy, sc);

          return (
            <div key={pi} style={{ flex: '1', minWidth: '155px', background: '#fff', border: `2px solid ${color}`, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontWeight: '700', fontSize: '0.82rem', color, marginBottom: '2px' }}>{label}</div>
              <div style={{ fontFamily: "'Courier New',monospace", fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>{eq}</div>
              <div style={{ fontFamily: "'Courier New',monospace", fontSize: '0.88rem', color, fontWeight: '700', marginBottom: '4px' }}>{filled}</div>
              <svg width={cW} height={cH} viewBox={`0 0 ${cW} ${cH}`} style={{ maxWidth: '100%' }}>
                {/* Light grid */}
                {[-1,0,1,2,3,4].map(x => <line key={x} x1={toX(x,ox,sc)} y1={10} x2={toX(x,ox,sc)} y2={cH-10} stroke="#f0f0f0" strokeWidth="1"/>)}
                {[-1,0,1,2,3,4,5].map(y => <line key={y} x1={10} y1={toY(y,oy,sc)} x2={cW-10} y2={toY(y,oy,sc)} stroke="#f0f0f0" strokeWidth="1"/>)}
                {/* Axes */}
                <line x1={10} y1={oy} x2={cW-10} y2={oy} stroke="#ccc" strokeWidth="1.3"/>
                <line x1={ox} y1={cH-10} x2={ox} y2={8} stroke="#ccc" strokeWidth="1.3"/>
                {/* Line */}
                <path d={path} fill="none" stroke={color} strokeWidth="2.3"/>
                {/* Highlight based on form */}
                {highlight === 'b' && (
                  <g>
                    <circle cx={toX(0,ox,sc)} cy={toY(yInt,oy,sc)} r={6} fill={color} stroke="#fff" strokeWidth="1.5"/>
                    <text x={toX(0,ox,sc)+8} y={toY(yInt,oy,sc)-4} fontSize="10" fill={color} fontWeight="700">(0,{yInt})</text>
                  </g>
                )}
                {highlight === 'point' && (
                  <g>
                    <circle cx={ptSx} cy={ptSy} r={6} fill={color} stroke="#fff" strokeWidth="1.5"/>
                    <text x={ptSx+8} y={ptSy-4} fontSize="10" fill={color} fontWeight="700">({px},{py})</text>
                  </g>
                )}
                {/* x-intercept always */}
                <circle cx={toX(xInt,ox,sc)} cy={oy} r={4} fill="#aaa" stroke="#fff" strokeWidth="1"/>
              </svg>
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '2px', fontStyle: 'italic' }}>{note}</div>
            </div>
          );
        })}
      </div>
      <div style={S.dCaption}>
        All three describe the exact same line. Slope-intercept is easiest to graph. Point-slope is easiest when you know a point and slope. Standard form is used in systems of equations and linear programming.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Parallel and Perpendicular Lines
// ─────────────────────────────────────────────────────────────────
function DiagramParallelPerp() {
  const W = 520, H = 260, ox = 200, oy = 140, sc = 48;
  const toX = x => ox + x*sc;
  const toY = y => oy - y*sc;

  // Parallel: m=1 through y=0 and y=2
  // Perpendicular: m=1 through origin, m=-1 through origin
  const lines = [
    { m: 1,  b: 0,  color: '#2E75B6', dash: '',    label: 'y = x',       group: 'parallel' },
    { m: 1,  b: 2,  color: '#2E75B6', dash: '6,4', label: 'y = x + 2',   group: 'parallel' },
    { m: -1, b: 0,  color: '#c0392b', dash: '',    label: 'y = -x',      group: 'perp' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Parallel and Perpendicular Lines — Slope Relationships</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <Grid ox={ox} oy={oy} sc={sc} xTicks={[-3,-2,-1,0,1,2,3]} yTicks={[-2,-1,0,1,2,3]} W={W} H={H}/>

          {lines.map(({ m, b, color, dash }, i) => {
            const x1 = -3.5, x2 = 3.5;
            return (
              <line key={i}
                x1={toX(x1)} y1={toY(m*x1+b)}
                x2={toX(x2)} y2={toY(m*x2+b)}
                stroke={color} strokeWidth="2.3" strokeDasharray={dash}/>
            );
          })}

          {/* Right-angle marker at intersection of y=x and y=-x */}
          {(() => {
            const ix = 0, iy = 0; // intersection
            const size = 18;
            const angle = Math.PI/4;
            const p1x = toX(ix) + size*Math.cos(angle), p1y = toY(iy) - size*Math.sin(angle);
            const p2x = toX(ix) + size*Math.cos(angle - Math.PI/2), p2y = toY(iy) - size*Math.sin(angle - Math.PI/2);
            const pmx = p1x + p2x - toX(ix), pmy = p1y + p2y - toY(iy);
            return (
              <g>
                <path d={`M ${p1x},${p1y} L ${pmx},${pmy} L ${p2x},${p2y}`} fill="none" stroke="#e07b00" strokeWidth="1.5"/>
                <text x={toX(0.5)} y={toY(-0.6)} fontSize="11" fill="#e07b00" fontWeight="700">90°</text>
              </g>
            );
          })()}

          {/* Parallel label with double arrow */}
          <line x1={toX(-2.5)} y1={toY(-2.5)} x2={toX(-2.5)} y2={toY(-0.5)} stroke="#2E75B6" strokeWidth="1.3" strokeDasharray="3,3"/>
          <text x={toX(-2.5)-6} y={toY(-1.5)} textAnchor="end" fontSize="10" fill="#2E75B6" fontWeight="700">equal</text>
          <text x={toX(-2.5)-6} y={toY(-1.5)+12} textAnchor="end" fontSize="10" fill="#2E75B6">distance</text>

          {/* Legend */}
          <rect x={W-210} y={12} width={200} height={95} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={W-110} y={28} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Slope Rules</text>
          <line x1={W-202} y1={42} x2={W-182} y2={42} stroke="#2E75B6" strokeWidth="2.3"/>
          <line x1={W-202} y1={56} x2={W-182} y2={56} stroke="#2E75B6" strokeWidth="2.3" strokeDasharray="6,4"/>
          <text x={W-178} y={46} fontSize="10" fill="#2E75B6" fontFamily="'Courier New',monospace">m₁ = m₂ = 1  (parallel)</text>
          <text x={W-178} y={60} fontSize="10" fill="#2E75B6" fontFamily="'Courier New',monospace">same slope, never cross</text>
          <line x1={W-202} y1={74} x2={W-182} y2={74} stroke="#c0392b" strokeWidth="2.3"/>
          <text x={W-178} y={78} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">m = -1  (perpendicular to m=1)</text>
          <text x={W-178} y={90} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">m₁ · m₂ = (1)(-1) = -1</text>

          {/* Line labels */}
          {lines.map(({ m, b, color, label }, i) => {
            const lx = 3.0, ly = m*lx + b;
            return (
              <text key={i} x={toX(lx)+5} y={toY(ly)} fontSize="11" fill={color} fontWeight="700">{label}</text>
            );
          })}
        </svg>
      </div>
      <div style={S.dCaption}>
        Parallel lines have identical slopes and never intersect. Perpendicular lines have slopes that are negative reciprocals — their product is always -1. The right-angle marker at the intersection confirms the 90° angle.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Systems of Linear Equations (3 cases)
// ─────────────────────────────────────────────────────────────────
function DiagramSystems() {
  const cW = 160, cH = 170;
  const ox = 80, oy = 90, sc = 30;
  const toX = x => ox + x*sc;
  const toY = y => oy - y*sc;

  const cases = [
    {
      label: 'One Solution',
      sub: 'Lines intersect',
      color: '#27ae60',
      note: 'consistent & independent',
      lines: [
        { m: 1,   b: 0,  color: '#2E75B6' },
        { m: -0.5, b: 1.5, color: '#c0392b' },
      ],
      intersection: { x: 1, y: 1 },
    },
    {
      label: 'No Solution',
      sub: 'Parallel lines',
      color: '#c0392b',
      note: 'inconsistent',
      lines: [
        { m: 1, b: 0.5,  color: '#2E75B6' },
        { m: 1, b: -0.8, color: '#c0392b' },
      ],
      intersection: null,
    },
    {
      label: 'Infinite Solutions',
      sub: 'Same line',
      color: '#e07b00',
      note: 'consistent & dependent',
      lines: [
        { m: 1, b: 0, color: '#2E75B6', width: 4 },
        { m: 1, b: 0, color: '#e07b00', dash: '6,3', width: 2 },
      ],
      intersection: null,
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Systems of Linear Equations — Three Possible Outcomes</div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {cases.map(({ label, sub, color, note, lines, intersection }, ci) => (
          <div key={ci} style={{ flex: '1', minWidth: '148px', background: '#fff', border: `2px solid ${color}`, borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
            <div style={{ fontWeight: '700', fontSize: '0.85rem', color, marginBottom: '2px' }}>{label}</div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>{sub}</div>
            <svg width={cW} height={cH} viewBox={`0 0 ${cW} ${cH}`} style={{ maxWidth: '100%' }}>
              {/* Light axes */}
              <line x1={10} y1={oy} x2={cW-10} y2={oy} stroke="#ddd" strokeWidth="1.2"/>
              <line x1={ox} y1={10} x2={ox} y2={cH-10} stroke="#ddd" strokeWidth="1.2"/>
              {/* Lines */}
              {lines.map(({ m, b, color: lc, dash = '', width = 2 }, li) => (
                <line key={li}
                  x1={toX(-2.8)} y1={toY(m*-2.8+b)}
                  x2={toX(2.8)} y2={toY(m*2.8+b)}
                  stroke={lc} strokeWidth={width} strokeDasharray={dash}/>
              ))}
              {/* Intersection dot */}
              {intersection && (
                <g>
                  <circle cx={toX(intersection.x)} cy={toY(intersection.y)} r={7} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
                  <text x={toX(intersection.x)+9} y={toY(intersection.y)-4} fontSize="10" fill="#27ae60" fontWeight="700">
                    ({intersection.x},{intersection.y})
                  </text>
                </g>
              )}
            </svg>
            <div style={{ fontSize: '0.73rem', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>{note}</div>
          </div>
        ))}
      </div>
      <div style={S.dCaption}>
        A system of two linear equations has exactly three outcomes. The intersection point, if it exists, is the (x, y) pair that satisfies both equations simultaneously. These cases appear in every field of engineering when balancing two constraints.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Linear Interpolation
// ─────────────────────────────────────────────────────────────────
function DiagramInterpolation() {
  const W = 500, H = 260, ox = 60, oy = 210, sc = 55;
  const toX = x => ox + x*sc;
  const toY = y => oy - y*sc;

  // Known points: (1, 1.5) and (4, 3.5)
  // Interpolate at x=2.5
  const x0 = 1, y0 = 1.5, x1 = 4, y1 = 3.5;
  const xi = 2.5;
  const yi = y0 + (y1 - y0) * (xi - x0) / (x1 - x0);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear Interpolation — Estimating Between Two Known Points</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <Grid ox={ox} oy={oy} sc={sc} xTicks={[0,1,2,3,4,5]} yTicks={[0,1,2,3,4]} W={W} H={H}/>

          {/* Actual curve (unknown, slightly nonlinear) */}
          {(() => {
            const pts = [];
            for (let x = 0.5; x <= 4.8; x += 0.05) {
              const y = 1.0 + 0.6*x + 0.04*Math.sin(x*2.5);
              pts.push(`${toX(x)},${toY(y)}`);
            }
            return <path d={'M '+pts.join(' L ')} fill="none" stroke="#ddd" strokeWidth="2" strokeDasharray="5,4"/>;
          })()}

          {/* Interpolation line between known points */}
          <line x1={toX(x0)} y1={toY(y0)} x2={toX(x1)} y2={toY(y1)}
            stroke="#2E75B6" strokeWidth="2.3"/>

          {/* Known points */}
          {[[x0,y0,'known'],[x1,y1,'known']].map(([x,y,lbl], i) => (
            <g key={i}>
              <circle cx={toX(x)} cy={toY(y)} r={7} fill="#1F3864" stroke="#fff" strokeWidth="2"/>
              <text x={toX(x)+9} y={toY(y)-5} fontSize="11" fill="#1F3864" fontWeight="700">({x}, {y})</text>
              <text x={toX(x)+9} y={toY(y)+7} fontSize="9" fill="#888">known</text>
            </g>
          ))}

          {/* Interpolated point */}
          <line x1={toX(xi)} y1={oy} x2={toX(xi)} y2={toY(yi)}
            stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
          <line x1={ox} y1={toY(yi)} x2={toX(xi)} y2={toY(yi)}
            stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
          <circle cx={toX(xi)} cy={toY(yi)} r={7} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
          <text x={toX(xi)+9} y={toY(yi)-5} fontSize="11" fill="#c0392b" fontWeight="700">({xi}, {yi.toFixed(2)})</text>
          <text x={toX(xi)+9} y={toY(yi)+7} fontSize="9" fill="#c0392b">interpolated</text>

          {/* x label on axis */}
          <text x={toX(xi)} y={oy+15} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">x = {xi}</text>

          {/* True curve label */}
          <text x={toX(4.4)} y={toY(3.9)} fontSize="10" fill="#bbb" fontStyle="italic">true curve</text>
          <text x={toX(4.4)} y={toY(3.9)+12} fontSize="9" fill="#bbb">(unknown)</text>

          {/* Formula box */}
          <rect x={10} y={14} width={240} height={52} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={120} y={30} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Linear Interpolation Formula:</text>
          <text x={120} y={46} textAnchor="middle" fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">y = y₀ + (y₁-y₀)(x-x₀)/(x₁-x₀)</text>
          <text x={120} y={60} textAnchor="middle" fontSize="10" fill="#555" fontFamily="'Courier New',monospace">= 1.5 + (3.5-1.5)(2.5-1)/(4-1) = {yi.toFixed(2)}</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Interpolation assumes the unknown value lies on a straight line between two known points. It is used constantly in engineering — reading material property tables, converting sensor readings, and approximating between data points.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — ME Applications
// ─────────────────────────────────────────────────────────────────
function DiagramMEApplications() {
  const cW = 160, cH = 150;
  const pad = { l: 34, r: 12, t: 18, b: 30 };
  const iW = cW - pad.l - pad.r, iH = cH - pad.t - pad.b;

  const apps = [
    {
      title: "Stress-Strain (Elastic)",
      eq: "σ = E · ε",
      color: '#2E75B6',
      pts: [[0,0],[0.002,400],[0.004,800],[0.006,1200]],
      xLabel: 'ε (strain)', yLabel: 'σ',
      note: 'slope = E (Young\'s modulus)',
      xMax: 0.008, yMax: 1600,
    },
    {
      title: "Spring Force",
      eq: "F = k · x",
      color: '#c0392b',
      pts: [[0,0],[1,200],[2,400],[3,600]],
      xLabel: 'x (disp)', yLabel: 'F',
      note: 'slope = k (spring constant)',
      xMax: 4, yMax: 800,
    },
    {
      title: "Resistance vs Temp",
      eq: "R = R₀(1 + αT)",
      color: '#27ae60',
      pts: [[0,100],[50,120],[100,140],[150,160]],
      xLabel: 'T (°C)', yLabel: 'R',
      note: 'slope = R₀·α',
      xMax: 200, yMax: 180,
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear Functions in Mechanical Engineering</div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {apps.map(({ title, eq, color, pts, xLabel, yLabel, note, xMax, yMax }, ci) => {
          const toX = v => pad.l + (v / xMax) * iW;
          const toY = v => pad.t + iH - (v / yMax) * iH;
          const linePts = pts.map(([x, y]) => `${toX(x)},${toY(y)}`).join(' L ');

          return (
            <div key={ci} style={{ flex: '1', minWidth: '148px', background: '#fff', border: '1px solid #ccd9ea', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontWeight: '700', fontSize: '0.8rem', color, marginBottom: '2px' }}>{title}</div>
              <div style={{ fontFamily: "'Courier New',monospace", fontSize: '0.72rem', color: '#888', marginBottom: '4px' }}>{eq}</div>
              <svg width={cW} height={cH} viewBox={`0 0 ${cW} ${cH}`} style={{ maxWidth: '100%' }}>
                {/* Axes */}
                <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.3"/>
                <line x1={pad.l} y1={pad.t+iH} x2={pad.l+iW} y2={pad.t+iH} stroke="#bbb" strokeWidth="1.3"/>
                <text x={pad.l+iW+2} y={pad.t+iH+4} fontSize="8" fill="#aaa">{xLabel}</text>
                <text x={pad.l-3} y={pad.t+4} textAnchor="end" fontSize="8" fill="#aaa">{yLabel}</text>
                {/* Area fill */}
                <path d={`M ${toX(pts[0][0])},${toY(pts[0][1])} L ${linePts.split(' L ').slice(1).join(' L ')} L ${toX(pts[pts.length-1][0])},${pad.t+iH} L ${toX(pts[0][0])},${pad.t+iH} Z`}
                  fill={color} fillOpacity="0.1"/>
                {/* Line */}
                <polyline points={linePts} fill="none" stroke={color} strokeWidth="2.3"/>
                {/* Data points */}
                {pts.map(([x, y], i) => (
                  <circle key={i} cx={toX(x)} cy={toY(y)} r={3.5} fill={color} stroke="#fff" strokeWidth="1"/>
                ))}
                {/* Slope annotation */}
                {(() => {
                  const [x1,y1] = pts[1], [x2,y2] = pts[2];
                  const sx1 = toX(x1), sy1 = toY(y1), sx2 = toX(x2), sy2 = toY(y2);
                  return (
                    <g>
                      <line x1={sx1} y1={sy1} x2={sx2} y2={sy1} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,2"/>
                      <line x1={sx2} y1={sy1} x2={sx2} y2={sy2} stroke="#e07b00" strokeWidth="1" strokeDasharray="3,2"/>
                    </g>
                  );
                })()}
              </svg>
              <div style={{ fontSize: '0.72rem', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>{note}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: '14px', background: '#EBF3FB', border: '1px solid #2E75B6', borderRadius: '6px', padding: '14px' }}>
        <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1F3864', marginBottom: '8px', fontFamily: "'Courier New',monospace" }}>
          The Slope IS the Engineering Constant
        </div>
        {[
          ['Stress-Strain:    slope = E', "Young's Modulus — stiffness of the material"],
          ['Force-Disp:       slope = k', 'Spring constant — resistance to deformation'],
          ['Voltage-Current:  slope = R', "Ohm's Law — resistance of the conductor"],
          ['Temp-Resistance:  slope = R₀α', 'Temperature coefficient of resistance'],
          ['Load-Deflection:  slope = EI/L³', 'Structural stiffness — beam or column'],
        ].map(([label, desc], i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', padding: '5px 0', borderBottom: i < 4 ? '1px solid #d0e4f7' : 'none' }}>
            <div style={{ fontFamily: "'Courier New',monospace", fontSize: '0.85rem', color: '#2E75B6', fontWeight: '600', minWidth: '200px' }}>{label}</div>
            <div style={{ fontSize: '0.85rem', color: '#555' }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function LinearFunctions(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Linear Functions</h1>

      <p style={S.p}>
        A linear function produces a straight line when graphed. It is the simplest and most common relationship in engineering — whenever two quantities change at a constant rate relative to each other, the relationship is linear. The slope of that line is usually a physical constant: stiffness, resistance, modulus, or rate.
      </p>

      <InfoBox
        title="Slope-Intercept Form:  y = mx + b"
        items={[
          "m = slope — rate of change, rise over run (Δy/Δx)",
          "b = y-intercept — value of y when x = 0",
          "Linear means degree 1 — the highest power of x is 1",
          "A constant slope means equal changes in y for equal changes in x",
        ]}
      />

      {/* Anatomy */}
      <h2 style={S.h2}>Line Anatomy</h2>
      <DiagramAnatomy />

      <Table2 rows={[
        ["Slope",              "m = (y₂ - y₁) / (x₂ - x₁) = rise / run\nSame value between any two points on the line"],
        ["Y-intercept",        "b — where line crosses the y-axis\nFound by setting x = 0"],
        ["X-intercept",        "Where line crosses the x-axis\nFound by setting y = 0, solving for x:  x = -b/m"],
        ["Positive slope",     "Line rises left to right  (m > 0)"],
        ["Negative slope",     "Line falls left to right  (m < 0)"],
        ["Zero slope",         "Horizontal line  y = b  (m = 0)"],
        ["Undefined slope",    "Vertical line  x = c — not a function"],
      ]} />

      {/* Three Forms */}
      <h2 style={S.h2}>Three Forms of a Line</h2>
      <DiagramForms />

      <Table2 rows={[
        ["Slope-intercept",    "y = mx + b\nBest for: graphing, identifying slope and y-intercept quickly"],
        ["Point-slope",        "y - y₁ = m(x - x₁)\nBest for: when you know a point and slope but not b\nRearranges to slope-intercept: y = m(x-x₁) + y₁"],
        ["Standard form",      "Ax + By = C  (A, B, C integers, A ≥ 0)\nBest for: systems of equations, linear programming\nSlope = -A/B,  y-intercept = C/B"],
        ["Two-point form",     "y - y₁ = [(y₂-y₁)/(x₂-x₁)] · (x - x₁)\nDerive slope first, then use point-slope"],
        ["Converting forms",   "All forms describe the same line — just rearrange algebraically\nSlope-intercept is usually the target form"],
      ]} />

      {/* Parallel and Perpendicular */}
      <h2 style={S.h2}>Parallel and Perpendicular Lines</h2>
      <DiagramParallelPerp />

      <Table2 rows={[
        ["Parallel lines",     "Same slope: m₁ = m₂\nDifferent y-intercepts — never intersect\nExample: y = 2x + 1  and  y = 2x - 3"],
        ["Perpendicular lines","Negative reciprocal slopes: m₁ · m₂ = -1\nm₂ = -1/m₁\nExample: m = 3 and m = -1/3"],
        ["Perpendicular check","Multiply the two slopes — if product = -1, they are perpendicular"],
        ["ME use",             "Perpendicular bisectors appear in mechanism design\nParallel constraints appear in linkages, rails, and tolerancing"],
      ]} />

      {/* Systems */}
      <h2 style={S.h2}>Systems of Linear Equations</h2>
      <DiagramSystems />

      <Table2 rows={[
        ["One solution",       "Lines intersect at exactly one point\nSolve by substitution or elimination to find (x, y)"],
        ["No solution",        "Lines are parallel — same slope, different intercepts\nContradiction when solving: 0 = constant"],
        ["Infinite solutions", "Lines are identical — same slope and intercept\nIdentity when solving: 0 = 0"],
        ["Substitution method","Solve one equation for y, substitute into the other\nBest when one equation is already solved for a variable"],
        ["Elimination method", "Multiply equations to match a coefficient, then add/subtract to cancel it\nBest for standard form equations"],
        ["Matrix method",      "[A]{x} = {b}  →  {x} = [A]⁻¹{b}\nBest for 3+ equations — see Matrices section"],
      ]} />

      {/* Interpolation */}
      <h2 style={S.h2}>Linear Interpolation</h2>
      <DiagramInterpolation />

      <Table2 rows={[
        ["Interpolation formula","y = y₀ + (y₁ - y₀) · (x - x₀) / (x₁ - x₀)\nEstimates y at any x between two known points"],
        ["When to use it",      "Reading between rows in a material properties table\nConverting between sensor output and physical units\nApproximating a function when only data points are known"],
        ["Assumption",          "Assumes a straight line between the two points\nMore accurate when points are close together"],
        ["Extrapolation",       "Extending beyond the known range — much less reliable\nAlways flag extrapolated values in engineering reports"],
        ["Error",               "Interpolation error depends on how curved the true function is\nQuadratic or spline interpolation is more accurate for curved data"],
      ]} />

      {/* ME Applications */}
      <h2 style={S.h2}>Mechanical Engineering Applications</h2>
      <DiagramMEApplications />

      <InfoBox
        title="Key Takeaway — In Linear Engineering Relationships, the Slope Is the Property"
        items={[
          "Hooke's Law:  F = kx — spring constant k is the slope of force vs displacement",
          "Ohm's Law:  V = IR — resistance R is the slope of voltage vs current",
          "Young's Modulus:  σ = Eε — modulus E is the slope of stress vs strain in the elastic region",
          "Thermal expansion:  ΔL = αL₀ΔT — coefficient α is the slope of length change vs temperature",
          "If you plot two engineering quantities and get a straight line, the slope is a material or system constant worth knowing",
        ]}
      />
    </div>
  );
}